import React, { useState, useRef, useEffect } from 'react'
import { Paperclip, Link as LinkIcon, ArrowRight, X, FileText, Loader2, Lock, ChevronDown, Youtube, Check, Globe, EyeOff, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../lib/utils'
import Button from './ui/button'
import type { StyleType } from '../types/podcast-generation'
import LanguagePicker from './language-picker'
import UpgradeDialog from './billing/upgrade-dialog'
import { useAuth } from '../hooks/use-auth'
import { PLAN_CONSTANTS } from '../types/auth'
import { createPortal } from 'react-dom'
import Tooltip from './ui/tooltip'
import { StylePicker } from './style-picker'

interface PodcastInputProps {
    onGenerate: (data: {
        prompt: string
        url?: string
        file?: File
        style: StyleType
        durationMinutes: number
        languageIso: string
        isPublic: boolean
    }) => Promise<void>
    isGenerating: boolean
    /** Optional initial values for pre-filling the input */
    initialValues?: {
        prompt?: string
        url?: string
        style?: StyleType
        durationMinutes?: number
    } | null
    /** Callback to clear initial values after they've been applied */
    onInitialValuesApplied?: () => void
    /** Error message to display in the form (e.g., from API errors) */
    error?: string | null
    /** Callback to clear the error */
    onErrorClear?: () => void
}

const DURATIONS = [5, 10, 20, 30]

const SAMPLE_PROMPTS = [
    "Explain quantum computing like I am a curious beginner",
    "Create a deep dive on the psychology of habit formation",
    "Summarize the key takeaways from my economics notes",
    "Turn this week's tech news into a podcast episode",
    "Help me understand stoic philosophy in a conversation",
    "Break down the pros and cons of remote work",
];

export function PodcastInput({ onGenerate, isGenerating, initialValues, onInitialValuesApplied, error, onErrorClear }: PodcastInputProps) {
    const { user } = useAuth()
    const isFreeUser = !user || user.plan === PLAN_CONSTANTS.FREE_PLAN

    const [prompt, setPrompt] = useState('')
    const [url, setUrl] = useState('')
    const [file, setFile] = useState<File | null>(null)

    // Settings
    const [selectedStyle, setSelectedStyle] = useState<StyleType>('conversational')
    const [duration, setDuration] = useState(5)
    const [languageIso, setLanguageIso] = useState('en')
    // Visibility: private (isPublic=false) for paid, public (isPublic=true) for free
    const [isPublic, setIsPublic] = useState(isFreeUser)

    // Apply initial values when provided
    const appliedInitialRef = useRef(false)
    useEffect(() => {
        if (initialValues && !appliedInitialRef.current) {
            appliedInitialRef.current = true
            if (initialValues.prompt) setPrompt(initialValues.prompt)
            if (initialValues.url) setUrl(initialValues.url)
            if (initialValues.style) setSelectedStyle(initialValues.style)
            if (initialValues.durationMinutes && !isFreeUser) setDuration(initialValues.durationMinutes)
            onInitialValuesApplied?.()
        }
    }, [initialValues, isFreeUser, onInitialValuesApplied])
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
    const [showPrivatePaywall, setShowPrivatePaywall] = useState(false)

    // Compact duration dropdown
    const [durationOpen, setDurationOpen] = useState(false)
    const durationTriggerRef = useRef<HTMLButtonElement>(null)
    const [durationPortalStyle, setDurationPortalStyle] = useState<{ top: number; left: number; width: number } | null>(null)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Typewriter state
    const [placeholderIndex, setPlaceholderIndex] = useState(0)
    const [placeholderText, setPlaceholderText] = useState("")
    const [isDeleting, setIsDeleting] = useState(false)
    const [isPaused, setIsPaused] = useState(false)

    // Typewriter effect
    useEffect(() => {
        const currentPrompt = SAMPLE_PROMPTS[placeholderIndex]

        if (isPaused) {
            const pauseTimer = setTimeout(() => {
                setIsPaused(false)
                setIsDeleting(true)
            }, 2500)
            return () => clearTimeout(pauseTimer)
        }

        if (isDeleting) {
            if (placeholderText.length === 0) {
                setIsDeleting(false)
                setPlaceholderIndex((prev) => (prev + 1) % SAMPLE_PROMPTS.length)
                return
            }
            const deleteTimer = setTimeout(() => {
                setPlaceholderText((prev) => prev.slice(0, -1))
            }, 20)
            return () => clearTimeout(deleteTimer)
        }

        if (placeholderText.length === currentPrompt.length) {
            setIsPaused(true)
            return
        }

        const typeTimer = setTimeout(() => {
            setPlaceholderText(currentPrompt.slice(0, placeholderText.length + 1))
        }, 45 + Math.random() * 30)

        return () => clearTimeout(typeTimer)
    }, [placeholderText, isDeleting, isPaused, placeholderIndex])


    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
        }
    }, [prompt])

    const computeDurationPosition = React.useCallback(() => {
        const el = durationTriggerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const width = Math.max(rect.width, 150)
        setDurationPortalStyle({ top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX, width })
    }, [])

    useEffect(() => {
        if (!durationOpen) return
        const handle = () => computeDurationPosition()
        window.addEventListener('resize', handle)
        window.addEventListener('scroll', handle, true)
        return () => {
            window.removeEventListener('resize', handle)
            window.removeEventListener('scroll', handle, true)
        }
    }, [durationOpen, computeDurationPosition])

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0]
            const fileName = selectedFile.name.toLowerCase()
            const fileExtension = '.' + fileName.split('.').pop()
            
            // Validate file type
            const isValidExtension = SUPPORTED_FILE_EXTENSIONS.includes(fileExtension)
            const isValidMimeType = SUPPORTED_MIME_TYPES.includes(selectedFile.type)
            
            if (!isValidExtension && !isValidMimeType) {
                setFileError(`Unsupported file type. Please upload one of: ${SUPPORTED_FILE_EXTENSIONS.join(', ')}`)
                // Reset the input so the same file can be selected again
                if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                }
                return
            }
            
            setFileError('')
            setFile(selectedFile)
            // Reset URL if file is chosen
            setUrl('')
        }
    }

    // Better URL input handling
    const [isUrlInputOpen, setIsUrlInputOpen] = useState(false)
    const [isYouTubeInputOpen, setIsYouTubeInputOpen] = useState(false)
    const [urlError, setUrlError] = useState('')
    const [youtubeError, setYoutubeError] = useState('')
    const [fileError, setFileError] = useState('')
    const urlInputRef = useRef<HTMLInputElement>(null)
    const youtubeInputRef = useRef<HTMLInputElement>(null)

    // Supported file formats matching backend generator
    const SUPPORTED_FILE_EXTENSIONS = ['.txt', '.md', '.json', '.pdf', '.docx', '.pptx']
    const SUPPORTED_MIME_TYPES = [
        'text/plain',
        'text/markdown',
        'application/json',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ]

    const isValidUrl = (u: string) => {
        try {
            const parsed = new URL(u)
            return parsed.protocol === 'http:' || parsed.protocol === 'https:'
        } catch {
            return false
        }
    }

    const clearAttachment = () => {
        setFile(null)
        setUrl('')
        setFileError('')
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!prompt.trim() && !file && !url) return

        // Clear any previous error when submitting
        onErrorClear?.()

        onGenerate({
            prompt,
            url: url || undefined,
            file: file || undefined,
            style: selectedStyle,
            durationMinutes: duration,
            languageIso,
            // Free users must always be public
            isPublic: isFreeUser ? true : isPublic,
        })
    }

    // Handle visibility toggle with paywall for free users
    const handleVisibilityToggle = () => {
        if (isPublic) {
            // Trying to make private
            if (isFreeUser) {
                setShowPrivatePaywall(true)
                return
            }
            setIsPublic(false)
        } else {
            // Making public is always allowed
            setIsPublic(true)
        }
    }

    // Enforce free-plan duration cap
    useEffect(() => {
        if (isFreeUser && duration !== 5) {
            setDuration(5)
        }
    }, [isFreeUser, duration])

    const isYouTubeUrl = (u: string) => /youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed/.test(u)

    const activePlaceholder = React.useMemo(() => {
        if (file) return "Add context for this document..."
        if (url && isYouTubeUrl(url)) return "Add context for this video..."
        if (url) return "Add context for this link..."
        return placeholderText
    }, [file, url, placeholderText])

    return (
        <div className="w-full max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="relative bg-white rounded-2xl shadow-xl shadow-black/5 border border-black/5 overflow-hidden transition-all hover:shadow-2xl hover:shadow-emerald-900/5 focus-within:ring-2 focus-within:ring-emerald-500/20">

                {/* Main Input Area */}
                <div className="p-3 sm:p-4 md:p-6">
                    <div className="relative">
                        <textarea
                            ref={textareaRef}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={activePlaceholder}
                            className="w-full min-h-[50px] sm:min-h-[60px] max-h-[200px] sm:max-h-[300px] resize-none border-none bg-transparent text-base sm:text-lg md:text-xl placeholder:text-gray-300 focus:ring-0 focus:outline-none p-0 text-foreground font-medium"
                            rows={1}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    handleSubmit(e)
                                }
                            }}
                        />
                    </div>

                    {/* Attachments Display */}
                    <AnimatePresence>
                        {(file || url) && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="flex items-center gap-2 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100"
                            >
                                {file && (
                                    <div className="flex items-center gap-1.5 sm:gap-2 bg-emerald-50 text-emerald-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium">
                                        <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                                        <span className="truncate max-w-[120px] sm:max-w-[200px]">{file.name}</span>
                                        <button type="button" onClick={clearAttachment} className="ml-0.5 sm:ml-1 hover:text-emerald-900 cursor-pointer"><X className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></button>
                                    </div>
                                )}
                                {url && isYouTubeUrl(url) && (
                                    <div className="flex items-center gap-1.5 sm:gap-2 bg-red-50 text-red-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium">
                                        <Youtube className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                                        <span className="truncate max-w-[120px] sm:max-w-[200px]">{url}</span>
                                        <button type="button" onClick={clearAttachment} className="ml-0.5 sm:ml-1 hover:text-red-900 cursor-pointer"><X className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></button>
                                    </div>
                                )}
                                {url && !isYouTubeUrl(url) && (
                                    <div className="flex items-center gap-1.5 sm:gap-2 bg-blue-50 text-blue-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium">
                                        <LinkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                                        <span className="truncate max-w-[120px] sm:max-w-[200px]">{url}</span>
                                        <button type="button" onClick={clearAttachment} className="ml-0.5 sm:ml-1 hover:text-blue-900 cursor-pointer"><X className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></button>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* File Error Message */}
                    <AnimatePresence>
                        {fileError && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="mt-3 sm:mt-4"
                            >
                                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm">
                                    <X className="w-4 h-4 shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="font-medium">Unsupported file type</p>
                                        <p className="text-red-600 mt-0.5">Please upload one of these formats: TXT, MD, JSON, PDF, DOCX, or PPTX</p>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => setFileError('')} 
                                        className="shrink-0 hover:text-red-900 cursor-pointer"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* API Error Message */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="mt-3 sm:mt-4"
                            >
                                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm">
                                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="font-medium">Unable to create podcast</p>
                                        <p className="text-red-600 mt-0.5">{error}</p>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => onErrorClear?.()} 
                                        className="shrink-0 hover:text-red-900 cursor-pointer"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* URL Input Popover */}
                    <AnimatePresence>
                        {isUrlInputOpen && !url && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                            >
                                <div className="mt-4 pt-1 flex flex-col gap-1">
                                    <div className="relative flex items-center">
                                        <input
                                            ref={urlInputRef}
                                            type="url"
                                            placeholder="Paste website URL..."
                                            className={cn(
                                                "w-full bg-gray-50 border rounded-xl py-3 pl-4 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent",
                                                urlError ? "border-red-400" : "border-gray-200"
                                            )}
                                            onChange={() => urlError && setUrlError('')}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    const val = e.currentTarget.value.trim()
                                                    if (!val) return
                                                    if (!isValidUrl(val)) {
                                                        setUrlError('Please enter a valid URL')
                                                        return
                                                    }
                                                    setUrl(val)
                                                    setIsUrlInputOpen(false)
                                                    setUrlError('')
                                                    setFile(null)
                                                } else if (e.key === 'Escape') {
                                                    setIsUrlInputOpen(false)
                                                    setUrlError('')
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const val = urlInputRef.current?.value.trim()
                                                if (!val) return
                                                if (!isValidUrl(val)) {
                                                    setUrlError('Please enter a valid URL')
                                                    return
                                                }
                                                setUrl(val)
                                                setIsUrlInputOpen(false)
                                                setUrlError('')
                                                setFile(null)
                                            }}
                                            className="absolute right-2 w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 transition-all cursor-pointer"
                                            aria-label="Add URL"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {urlError && (
                                        <p className="text-xs text-red-500 text-left pl-1">{urlError}</p>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* YouTube URL Input Popover */}
                    <AnimatePresence>
                        {isYouTubeInputOpen && !url && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                            >
                                <div className="mt-4 pt-1 flex flex-col gap-1">
                                    <div className="relative flex items-center">
                                        <input
                                            ref={youtubeInputRef}
                                            type="url"
                                            placeholder="Paste YouTube URL..."
                                            className={cn(
                                                "w-full bg-red-50 border rounded-xl py-3 pl-4 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent",
                                                youtubeError ? "border-red-400" : "border-red-200"
                                            )}
                                            onChange={() => youtubeError && setYoutubeError('')}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    const val = e.currentTarget.value.trim()
                                                    if (!val) return
                                                    if (!isYouTubeUrl(val)) {
                                                        setYoutubeError('Please enter a valid YouTube URL')
                                                        return
                                                    }
                                                    setUrl(val)
                                                    setIsYouTubeInputOpen(false)
                                                    setYoutubeError('')
                                                    setFile(null)
                                                } else if (e.key === 'Escape') {
                                                    setIsYouTubeInputOpen(false)
                                                    setYoutubeError('')
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const val = youtubeInputRef.current?.value.trim()
                                                if (!val) return
                                                if (!isYouTubeUrl(val)) {
                                                    setYoutubeError('Please enter a valid YouTube URL')
                                                    return
                                                }
                                                setUrl(val)
                                                setIsYouTubeInputOpen(false)
                                                setYoutubeError('')
                                                setFile(null)
                                            }}
                                            className="absolute right-2 w-8 h-8 flex items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600 active:scale-95 transition-all cursor-pointer"
                                            aria-label="Add YouTube URL"
                                        >
                                            <Check className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {youtubeError && (
                                        <p className="text-xs text-red-500 text-left pl-1">{youtubeError}</p>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                </div>

                {/* Style Picker Section */}
                <div className="px-3 py-3 sm:px-4 sm:py-3 md:px-6 md:py-4 border-t border-gray-100">
                    <StylePicker
                        value={selectedStyle}
                        onChange={setSelectedStyle}
                        disabled={isGenerating}
                    />
                </div>

                {/* Toolbar / Footer */}
                <div className="bg-gray-50/50 px-3 py-3 sm:px-4 sm:py-3 md:px-6 md:py-4 border-t border-gray-100">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
                        
                        {/* Left Group: Attachments */}
                        <div className="flex items-center gap-1 sm:gap-1.5 w-full md:w-auto overflow-x-auto no-scrollbar">
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept=".txt,.md,.json,.pdf,.docx,.pptx,text/plain,text/markdown,application/json,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                onChange={handleFileChange}
                            />

                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center justify-center h-8 px-2 sm:h-9 sm:w-9 sm:px-0 lg:w-auto lg:px-2.5 rounded-lg text-gray-600 hover:bg-white hover:text-emerald-600 hover:shadow-sm transition-all cursor-pointer bg-transparent sm:hover:bg-white"
                                title="Attach file"
                            >
                                <Paperclip className="w-4 h-4 sm:w-4 sm:h-4" />
                                <span className="ml-1.5 text-xs sm:text-sm font-medium inline md:hidden lg:inline">File</span>
                            </button>

                            <div className="h-4 w-px bg-gray-200 mx-0.5 md:hidden lg:inline" />

                            <button
                                type="button"
                                onClick={() => {
                                    setIsUrlInputOpen(true)
                                    setIsYouTubeInputOpen(false)
                                    setTimeout(() => urlInputRef.current?.focus(), 100)
                                }}
                                className="flex items-center justify-center gap-1.5 h-8 px-2.5 sm:h-auto sm:px-2.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-gray-600 hover:bg-white hover:text-emerald-600 hover:shadow-sm transition-all cursor-pointer whitespace-nowrap"
                                title="Add website URL"
                            >
                                <LinkIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span className="inline md:hidden lg:inline">Website</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setIsYouTubeInputOpen(true)
                                    setIsUrlInputOpen(false)
                                    setTimeout(() => youtubeInputRef.current?.focus(), 100)
                                }}
                                className="flex items-center justify-center gap-1.5 h-8 px-2.5 sm:h-auto sm:px-2.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-red-600 hover:bg-red-50 hover:shadow-sm transition-all cursor-pointer whitespace-nowrap"
                                title="Add YouTube URL"
                            >
                                <Youtube className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span className="inline md:hidden lg:inline">YouTube</span>
                            </button>
                        </div>

                        {/* Right Group: Settings & Actions */}
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                            
                            {/* Settings Row */}
                            <div className="flex items-center justify-between sm:justify-start w-full md:w-auto gap-2 sm:gap-3 border-t sm:border-t-0 border-gray-100 pt-3 sm:pt-0">
                                {/* Language Picker */}
                                <div className="flex items-center shrink-0">
                                    <LanguagePicker
                                        value={languageIso}
                                        onChange={setLanguageIso}
                                        size="sm"
                                        mode="compact"
                                        disabled={isGenerating}
                                    />
                                </div>

                                <span className="text-gray-200 leading-none lg:inline hidden">|</span>
                                <span className="text-gray-200 leading-none md:hidden inline">|</span>

                                {/* Duration selector */}
                                <button
                                    ref={durationTriggerRef}
                                    type="button"
                                    disabled={isGenerating}
                                    onClick={() => {
                                        setDurationOpen(o => {
                                            const next = !o
                                            if (next) computeDurationPosition()
                                            return next
                                        })
                                    }}
                                    className={cn(
                                        'inline-flex h-8 md:h-9 items-center gap-1 md:gap-1.5 bg-transparent border-none text-xs md:text-sm font-medium leading-none text-gray-500 focus:outline-none cursor-pointer hover:text-emerald-600',
                                        isGenerating && 'opacity-50 cursor-not-allowed hover:text-gray-500'
                                    )}
                                    aria-haspopup="listbox"
                                    aria-expanded={durationOpen}
                                >
                                    <span className="hidden xs:inline">{duration} mins</span>
                                    <span className="xs:hidden">{duration}m</span>
                                    <ChevronDown className={cn('h-3.5 w-3.5 md:h-4 md:w-4 transition-transform', durationOpen && 'rotate-180')} />
                                </button>
                                {durationOpen && durationPortalStyle && createPortal(
                                    <>
                                        <div
                                            className="fixed inset-0 z-[990]"
                                            onClick={() => setDurationOpen(false)}
                                        />
                                        <div
                                            className="z-[1000] absolute"
                                            style={{ position: 'absolute', top: durationPortalStyle.top, left: durationPortalStyle.left, width: durationPortalStyle.width }}
                                        >
                                            <div className="rounded-lg border border-black/10 bg-white shadow-lg overflow-hidden">
                                                <div className="py-1">
                                                    {DURATIONS.map((d) => {
                                                        const selected = duration === d
                                                        const locked = isFreeUser && d !== 5

                                                        return (
                                                            <button
                                                                key={d}
                                                                type="button"
                                                                onClick={() => {
                                                                    if (locked) {
                                                                        setDurationOpen(false)
                                                                        setShowUpgradeDialog(true)
                                                                        return
                                                                    }
                                                                    setDuration(d)
                                                                    setDurationOpen(false)
                                                                }}
                                                                className={cn(
                                                                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors cursor-pointer',
                                                                    selected ? 'bg-emerald-50 text-emerald-700' : 'text-foreground hover:bg-black/5',
                                                                    locked && !selected && 'text-foreground/50'
                                                                )}
                                                                title={locked ? 'Upgrade to unlock longer episodes' : undefined}
                                                            >
                                                                <span>{d} mins</span>
                                                                {locked && (
                                                                    <span className="inline-flex items-center gap-1 text-xs text-foreground/60">
                                                                        <Lock className="h-3.5 w-3.5" />
                                                                    </span>
                                                                )}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </>,
                                    document.body
                                )}

                                <span className="text-gray-200 leading-none lg:inline hidden">|</span>
                                <span className="text-gray-200 leading-none md:hidden inline">|</span>

                                {/* Visibility Toggle - Moved to Settings Row on Mobile/Tablet */}
                                <Tooltip content={isPublic ? 'Make private' : 'Make public'}>
                                    <button
                                        type="button"
                                        onClick={handleVisibilityToggle}
                                        disabled={isGenerating}
                                        className={cn(
                                            'inline-flex h-9 items-center gap-1.5 bg-transparent border-none text-xs sm:text-sm font-medium leading-none focus:outline-none cursor-pointer transition-colors',
                                            isPublic 
                                                ? 'text-gray-500 hover:text-emerald-600'
                                                : 'text-emerald-600',
                                            isGenerating && 'opacity-50 cursor-not-allowed'
                                        )}
                                    >
                                        {isPublic ? (
                                            <>
                                                <Globe className="w-4 h-4" />
                                                <span className="hidden lg:inline">Public</span>
                                            </>
                                        ) : (
                                            <>
                                                <EyeOff className="w-4 h-4" />
                                                <span className="hidden lg:inline">Private</span>
                                            </>
                                        )}
                                        {isFreeUser && !isPublic && <Lock className="h-3 w-3 ml-0.5" />}
                                    </button>
                                </Tooltip>
                            </div>

                            {/* Separator on desktop only */}
                            <span className="text-gray-200 leading-none hidden lg:inline">|</span>

                            {/* Generate Button - Full width on mobile/tablet */}
                            <div className="flex items-center justify-end w-full sm:w-auto">
                                <Button
                                    disabled={(!prompt && !file && !url) || isGenerating}
                                    className={cn(
                                        "rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20 h-10 px-4 text-sm w-full sm:w-auto md:w-32 lg:w-32",
                                        isGenerating && "opacity-80"
                                    )}
                                >
                                    {isGenerating ? (
                                        <>
                                            <span className="inline mr-2">Generating</span>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            <span className="inline mr-2">Generate</span>
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            {/* Helper text */}
            <div className="mt-2 sm:mt-3 md:mt-4 flex justify-center sm:justify-start px-1 sm:px-2">
                <p className="text-[10px] sm:text-xs text-gray-400">
                    <span className="hidden sm:inline">1,750+ users · 150+ voices · 40+ languages</span>
                    <span className="sm:hidden">1,750+ users · 150+ voices</span>
                </p>
            </div>

            <UpgradeDialog
                open={showUpgradeDialog}
                onOpenChange={setShowUpgradeDialog}
                title="Upgrade to unlock longer episodes"
                bullets={["Unlock longer episodes and more advanced production features."]}
                paywallId="duration_longer_episodes"
            />

            <UpgradeDialog
                open={showPrivatePaywall}
                onOpenChange={setShowPrivatePaywall}
                title="Upgrade to make podcasts private"
                bullets={["Keep episodes private and share only with your audience when you choose."]}
                paywallId="private_podcasts"
            />
        </div>
    )
}
